"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon, XIcon } from "lucide-react";
import { AuthedImage } from "@/components/authed-image";
import { formatPhotoCredit } from "@/lib/kyiv";
import { cn } from "@/lib/utils";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

function clampZoom(value: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

export type ZoomPhoto = {
  url: string;
  createdAt?: string;
  authorName?: string;
};

export function ZoomPhotoDialog({
  src,
  photos,
  startIndex = 0,
  alt,
  onClose,
}: {
  src?: string;
  photos?: ZoomPhoto[];
  startIndex?: number;
  alt: string;
  onClose: () => void;
}) {
  const items = photos && photos.length > 0 ? photos : src ? [{ url: src }] : [];
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1);
  const [index, setIndex] = useState(() => Math.min(Math.max(0, startIndex), Math.max(0, items.length - 1)));
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const gesture = useRef<{
    mode: "none" | "pan" | "pinch";
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    startScale: number;
    startDist: number;
  }>({
    mode: "none",
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    startScale: 1,
    startDist: 0,
  });
  const lastTap = useRef(0);
  const zoomed = scale > MIN_ZOOM;
  const many = items.length > 1;
  const current = items[index];
  const credit = current
    ? formatPhotoCredit(current.createdAt ?? "", current.authorName ?? "")
    : "";

  function scrollToIndex(next: number, behavior: ScrollBehavior = "smooth") {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.min(Math.max(0, next), items.length - 1);
    el.scrollTo({ left: clamped * el.clientWidth, behavior });
    setIndex(clamped);
  }

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";

    let startX = 0;
    let startY = 0;
    function onTouchStart(event: TouchEvent) {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }
    function onTouchMove(event: TouchEvent) {
      if (event.touches.length !== 1) return;
      if (scaleRef.current > MIN_ZOOM) return;
      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      const scroller = scrollerRef.current;
      const atStart = !scroller || scroller.scrollLeft <= 1;
      const atEnd = !scroller || scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      const horizontal = Math.abs(dx) > Math.abs(dy);
      if (!horizontal || !many || (atStart && dx > 0) || (atEnd && dx < 0)) {
        event.preventDefault();
      }
    }
    overlay.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    overlay.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      overlay.removeEventListener("touchstart", onTouchStart, true);
      overlay.removeEventListener("touchmove", onTouchMove, true);
    };
  }, [many]);

  useLayoutEffect(() => {
    const initial = Math.min(Math.max(0, startIndex), Math.max(0, items.length - 1));
    setIndex(initial);
    setScale(1);
    setOffset({ x: 0, y: 0 });
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: initial * el.clientWidth, behavior: "auto" });
  }, [startIndex, items.length]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      setScale(1);
      setOffset({ x: 0, y: 0 });
      setIndex((current) => {
        const next = event.key === "ArrowLeft" ? current - 1 : current + 1;
        const clamped = Math.min(Math.max(0, next), items.length - 1);
        const el = scrollerRef.current;
        if (el) el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
        return clamped;
      });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length]);

  function zoomBy(factor: number) {
    setScale((current) => {
      const next = clampZoom(current * factor);
      if (next <= MIN_ZOOM) setOffset({ x: 0, y: 0 });
      return next;
    });
  }

  function onTouchStart(event: React.TouchEvent) {
    if (event.touches.length === 2) {
      const [a, b] = [event.touches[0], event.touches[1]];
      gesture.current = {
        mode: "pinch",
        startX: 0,
        startY: 0,
        originX: offset.x,
        originY: offset.y,
        startScale: scale,
        startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
      };
      return;
    }
    if (event.touches.length === 1 && scale > MIN_ZOOM) {
      gesture.current = {
        mode: "pan",
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        originX: offset.x,
        originY: offset.y,
        startScale: scale,
        startDist: 0,
      };
    }
  }

  function onTouchMove(event: React.TouchEvent) {
    const state = gesture.current;
    if (state.mode === "pinch" && event.touches.length === 2) {
      event.preventDefault();
      const [a, b] = [event.touches[0], event.touches[1]];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (state.startDist <= 0) return;
      const next = clampZoom(state.startScale * (dist / state.startDist));
      setScale(next);
      if (next <= MIN_ZOOM) setOffset({ x: 0, y: 0 });
      return;
    }
    if (state.mode === "pan" && event.touches.length === 1) {
      event.preventDefault();
      setOffset({
        x: state.originX + (event.touches[0].clientX - state.startX),
        y: state.originY + (event.touches[0].clientY - state.startY),
      });
    }
  }

  function onTouchEnd(event: React.TouchEvent) {
    if (event.touches.length === 0) {
      gesture.current.mode = "none";
      if (scale <= MIN_ZOOM) setOffset({ x: 0, y: 0 });
    } else if (event.touches.length === 1 && scale > MIN_ZOOM) {
      gesture.current = {
        mode: "pan",
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        originX: offset.x,
        originY: offset.y,
        startScale: scale,
        startDist: 0,
      };
    }
  }

  function onDoubleTap(clientX: number, clientY: number, rect: DOMRect) {
    if (scale > MIN_ZOOM) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
      return;
    }
    const next = 2.5;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setScale(next);
    setOffset({
      x: (centerX - clientX) * (next - 1),
      y: (centerY - clientY) * (next - 1),
    });
  }

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-[100] overscroll-none bg-black/90"
    >
      <div className="absolute top-3 right-3 z-10 flex gap-2">
        <button
          type="button"
          className="rounded-full bg-black/55 p-2.5 text-white disabled:opacity-40"
          aria-label="Зменшити"
          disabled={scale <= MIN_ZOOM}
          onClick={() => zoomBy(1 / 1.4)}
        >
          <MinusIcon className="size-5" />
        </button>
        <button
          type="button"
          className="rounded-full bg-black/55 p-2.5 text-white disabled:opacity-40"
          aria-label="Збільшити"
          disabled={scale >= MAX_ZOOM}
          onClick={() => zoomBy(1.4)}
        >
          <PlusIcon className="size-5" />
        </button>
        <button type="button" className="rounded-full bg-black/55 p-2.5 text-white" aria-label="Закрити" onClick={onClose}>
          <XIcon className="size-5" />
        </button>
      </div>
      {many ? (
        <>
          <button
            type="button"
            className="absolute top-1/2 left-2 z-10 hidden -translate-y-1/2 rounded-full bg-black/55 p-2 text-white disabled:opacity-30 wide:flex"
            aria-label="Попереднє фото"
            disabled={index <= 0}
            onClick={() => {
              setScale(1);
              setOffset({ x: 0, y: 0 });
              scrollToIndex(index - 1);
            }}
          >
            <ChevronLeftIcon className="size-5" />
          </button>
          <button
            type="button"
            className="absolute top-1/2 right-2 z-10 hidden -translate-y-1/2 rounded-full bg-black/55 p-2 text-white disabled:opacity-30 wide:flex"
            aria-label="Наступне фото"
            disabled={index >= items.length - 1}
            onClick={() => {
              setScale(1);
              setOffset({ x: 0, y: 0 });
              scrollToIndex(index + 1);
            }}
          >
            <ChevronRightIcon className="size-5" />
          </button>
        </>
      ) : null}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/75 to-transparent px-4 pt-10 pb-[max(1rem,env(safe-area-inset-bottom))] text-center">
        {credit ? <p className="text-sm font-medium text-white">{credit}</p> : null}
        <p className={cn("text-xs text-white/70", credit && "mt-1")}>
          {many ? `${index + 1} / ${items.length} · гортайте фото · щипок` : "Щипок або кнопки · подвійний тап"}
        </p>
      </div>
      <div
        ref={scrollerRef}
        className={cn(
          "flex h-full w-full",
          many && !zoomed && "snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-none touch-pan-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          (!many || zoomed) && "overflow-hidden",
        )}
        onScroll={(event) => {
          if (zoomed) return;
          const el = event.currentTarget;
          if (el.clientWidth <= 0) return;
          const next = Math.round(el.scrollLeft / el.clientWidth);
          if (next !== index) {
            setIndex(next);
            setScale(1);
            setOffset({ x: 0, y: 0 });
          }
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget && scale <= MIN_ZOOM) onClose();
        }}
      >
        {items.map((photo, photoIndex) => (
          <div
            key={`${photo.url}-${photoIndex}`}
            className={cn(
              "flex h-full w-full min-w-full shrink-0 items-center justify-center px-3 pt-3 pb-20",
              many && "snap-center",
            )}
            onClick={(event) => {
              if (event.target === event.currentTarget && scale <= MIN_ZOOM) onClose();
            }}
            onWheel={(event) => {
              if (photoIndex !== index) return;
              event.preventDefault();
              zoomBy(event.deltaY < 0 ? 1.12 : 1 / 1.12);
            }}
            onTouchStart={photoIndex === index ? onTouchStart : undefined}
            onTouchMove={photoIndex === index ? onTouchMove : undefined}
            onTouchEnd={photoIndex === index ? onTouchEnd : undefined}
            onTouchCancel={photoIndex === index ? onTouchEnd : undefined}
          >
            <div
              className="max-h-full max-w-full will-change-transform"
              style={
                photoIndex === index
                  ? {
                      transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                      transition: gesture.current.mode === "none" ? "transform 160ms ease-out" : "none",
                    }
                  : undefined
              }
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => {
                onDoubleTap(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());
              }}
              onTouchEnd={(event) => {
                if (photoIndex !== index) return;
                if (event.changedTouches.length !== 1 || event.touches.length > 0) return;
                const now = Date.now();
                if (now - lastTap.current < 280) {
                  const touch = event.changedTouches[0];
                  onDoubleTap(touch.clientX, touch.clientY, event.currentTarget.getBoundingClientRect());
                  lastTap.current = 0;
                } else {
                  lastTap.current = now;
                }
              }}
            >
              <AuthedImage
                src={photo.url}
                alt={alt}
                className="max-h-[min(92dvh,var(--app-height,92dvh))] max-w-[min(100vw,100%)] rounded-2xl object-contain select-none"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
